import { AfterViewInit, Component, inject, Input } from "@angular/core";
import { Cryptocoin, Fiat, FiatWallet, Offer } from "@models";
import { Router } from "@angular/router";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { SuccessComponent } from "../success.interface";
import { SavingsPlan } from "@models/savings_plan";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { Tradable } from "@models/interfaces/tradable";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import moment from "moment";
import { TRADE_TYPE } from "@models/trade-offer.model";
import { AccountService } from "@modules/account/services/account.service";
import { FiatService } from "@main-services/currency";
import { SavingsService } from "@main-services/savings.service";
import { SegmentService } from "@main-services/segment.service";
import { TradeSavingsComponent } from "@modules/trade/modals/trade-savings-modal/trade-savings-modal.component";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { combineLatest, Observable, tap } from "rxjs";
import { getStakeableHelper } from "@helpers/functions";
import { AutoStakingFacade } from "@modules/staking/domain/+state/auto-staking.facade";
import { StakingFacade } from "@modules/staking/domain/+state/staking.facade";
import { map } from "rxjs/operators";
import { WEB3_FEE_BENEFFIT_FLOW, Web3VSNFeeBenefitFF } from "@modules/web3/interface/web3.benefit.ui.interface";
import { LimitOrderNavigationFacade } from "@modules/limit-orders/services/navigation.facade";
import { MoSuccessVisionBannerState } from "@modules/web3/components/mo-success-vision-component/mo-success-vision-banner-state.model";
import { VsnSavingPlanState } from "@modules/trade/components/summary/vsn-summary.state";
import { NewProfileFacade } from "@modules/main/components/profile/new-profile/state/new-profile.facade";
import { Web3RemoteService } from "@modules/web3/services/web3.remote";
import { RudderstackService } from "@main-services/rudderstack.service";

@Component({
	selector: "savings-success",
	templateUrl: "./savings-success.component.html",
	styleUrls: ["./../success-screen.component.scss"],
	standalone: false
})
export class SavingsSuccessComponent extends SuccessComponent implements AfterViewInit {
	@Input()
	public data: SavingsPlan;

	private _fullFrequency: string = "";

	protected readonly flowName: string = "Savings";
	protected readonly pathPrepend: string = "/trade/savings";

	private launchDarklyService = inject(LaunchDarklyService);
	private readonly autoStakingFacade = inject(AutoStakingFacade);
	private readonly stakingFacade = inject(StakingFacade);
	private navigation = inject(LimitOrderNavigationFacade);
	private vsnSavingPlanState = inject(VsnSavingPlanState);
	private web3RemoteService = inject(Web3RemoteService);

	isVision = false;

	public showVisionWarning = false;
	public showVisionBanner = false;
	@Input()
	public visionFeeFromCookie: string = "0";
	@Input()
	public eligibleFromCookie: boolean = false;
	stakingVm$ = combineLatest({
		stakeables: this.stakingFacade.getStakeables$(),
		autoStakingState: this.autoStakingFacade.getOrFetchAutoStakingState$(),
		visionPid: this.getVisionPid$(),
		visionSavingFlag$: this.launchDarklyService.getFeatureConfig$<Web3VSNFeeBenefitFF>(
			LD_FLAG.WEB3_VSN_FEE_BENEFIT
		),
		visionSavingBenefit: this.vsnSavingPlanState.getVsnSavingPlan$(),
		settings: this.web3RemoteService.getUserSettings()
	}).pipe(
		tap((stakingVm) => {
			const stakeableHelper = getStakeableHelper(stakingVm.stakeables, this.data?.getCurrencyId());
			this.isVision =
				this.tradableListService.getItemById(this.data?.getCurrencyId())?.getPid() === stakingVm.visionPid;

			this.showVisionWarning =
				stakeableHelper.isStakeable &&
				!stakeableHelper.stakeable?.autoStakingEnabled &&
				stakingVm.autoStakingState.autoStakingSwitchOn &&
				this.isVision &&
				!this.showVisionBanner;

			this.showVisionBanner =
				(stakingVm?.visionSavingBenefit?.eligibleForVsn || this.eligibleFromCookie || this.isVision) &&
				!stakingVm.settings.visionBonus;
		})
	);

	// Vision Savings Benefit
	visionSavingBenefitFF$ = this.launchDarklyService
		.getFeatureConfig$<Web3VSNFeeBenefitFF>(LD_FLAG.WEB3_VSN_FEE_BENEFIT)
		.pipe(map((d) => d[WEB3_FEE_BENEFFIT_FLOW.savingsPlan]));
	VisionBannerState = MoSuccessVisionBannerState;
	visionSavingBenefit$ = this.vsnSavingPlanState.getVsnSavingPlan$();
	isUkUser$ = this.profileFacade.isUkResident$();

	public constructor(
		protected router: Router,
		protected tradableListService: TradableListService,
		protected fiatService: FiatService,
		protected accountService: AccountService,
		protected fiatwalletService: FiatWalletService,
		protected tradableWalletListService: TradableWalletListService,
		protected savingsService: SavingsService,
		protected segmentService: SegmentService,
		protected rudderstackService: RudderstackService,
		protected profileFacade: NewProfileFacade
	) {
		super(fiatService, tradableListService, accountService, router, segmentService, rudderstackService);
	}

	private getVisionPid$(): Observable<String> {
		return this.launchDarklyService
			.getFeatureConfig$<{
				pid: string;
			}>(LD_FLAG.VISION_PID)
			.pipe(map((data) => data.pid));
	}

	public ngAfterViewInit(): void {
		this._fullFrequency = this.getFullFrequencyFormatted();

		super.ngAfterViewInit();
	}

	public getFullFrequency(): string {
		return this._fullFrequency;
	}

	public goToSavings(): void {
		this.close();
		setTimeout(() => {
			// The Backbutten handling in Modals screws this if you dont delay it a little
			this.router.navigateByUrl("/savings");
		}, 300);
	}

	public addNewSavingsPlan(): void {
		setTimeout(() => {
			// The Backbutten handling in Modals screws this if you dont delay it a little
			const offer = new Offer();
			offer.setType(TRADE_TYPE.RECURRING_BUY);
			this.showModal(TradeSavingsComponent, offer);
		}, 300);
	}

	public showModal(component, data): void {
		const modal = this.modalService.create(component, { data });
		this.modalService.pop();
		modal.present();
	}

	public getTradable(): Tradable {
		const coinId = this.getCurrencyId();
		const coin = this.tradableListService.getItemById(coinId);
		if (!coin) {
			return new Cryptocoin();
		}
		return this.tradableListService.getItemById(coinId);
	}

	public getFiat(): Fiat {
		return this.fiatService.getItemById(this.getFiatId());
	}

	public getFiatWallet(walletId: string): FiatWallet {
		return this.fiatwalletService.getItemById(walletId);
	}

	public getFiatId(): string {
		return this.getFiatWallet(this.data.getFiatWalletId()).getFiatId();
	}

	public getAssetWallet(walletId: string): TradableWallet {
		return this.tradableWalletListService.getItemById(walletId);
	}

	public getCurrencyId(): string {
		return this.getAssetWallet(this.data.getAssetWalletId()).getCurrencyId();
	}

	public getFullFrequencyFormatted(): string {
		let fullFrequency: string = "";
		const mainFrequency: string = this.data.getFrequencyName();
		fullFrequency += mainFrequency + ", ";
		let dayFrequency: string;

		switch (this.data.getFrequency()) {
			case "monthly": {
				dayFrequency =
					moment.localeData().ordinal(+this.data.getDay()) +
					" " +
					this.translationService.translate("recurring_buy_modal_frequency_dropdown_day");
				break;
			}
			case "weekly":
			case "biweekly":
				dayFrequency = moment().isoWeekday(+this.data.getDay()).format("dddd");
				break;
			case "daily":
				dayFrequency = "";
				break;
			default:
				break;
		}

		if (dayFrequency) {
			fullFrequency += dayFrequency + ", ";
		}
		fullFrequency += this.data.getTime();
		fullFrequency = fullFrequency.substr(0, 1).toLowerCase() + fullFrequency.substr(1, fullFrequency.length);
		return fullFrequency;
	}

	public get fullDetailsPlan(): SavingsPlan {
		return this.savingsService.getItemById(this.data.getId());
	}

	public get isPlanActive(): boolean {
		return this.fullDetailsPlan ? this.fullDetailsPlan.isActive() : false;
	}

	public get isPlanInPending(): boolean {
		return this.fullDetailsPlan ? this.fullDetailsPlan.isPending() : false;
	}

	public getTitle(): string {
		return !this.getTradable().isToken()
			? this.translationService.translate("recurring_buy_success_screen_title", [
					this.getTradable().getSymbol(false, true)
				])
			: this.translationService.translate("recurring_buy_success_screen_title", [this.getTradable().getName()]);
	}

	public getDay(frequency: string): string {
		if (frequency !== "monthly") {
			return moment().isoWeekday(+this.data.getDay()).format("dddd");
		}
		return moment.localeData().ordinal(+this.data.getDay());
	}

	handleBannerClick(): void {
		this.close();
		this.navigation.gotoRootNavigation("/my-account/my-vision");
	}
}
